import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type {
	RoutingHistoryOptions,
	RoutingPolicyChange,
} from "@/types/admin-portal/routing-mode";
import { changedFields, groupByDay } from "./history-diff";

const timeFormat = new Intl.DateTimeFormat("id-ID", {
	timeZone: "Asia/Jakarta",
	hour: "2-digit",
	minute: "2-digit",
});

// groupByDay keys are YYYY-MM-DD in WIB; read back as a local date for the heading.
const dayLabel = (day: string) =>
	new Date(`${day}T00:00:00`).toLocaleDateString("id-ID", {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	});

function formatField(
	field: string,
	value: unknown,
	scope: RoutingPolicyChange["scope"],
	t: TFunction,
): string {
	if (value == null) {
		return field === "mode" ? t("locations.stored.default") : "—";
	}
	if (field === "mode") {
		return scope === "GLOBAL"
			? t(`global.modes.${value}.label`, { defaultValue: String(value) })
			: t(`locations.mode_label.${value}`, { defaultValue: String(value) });
	}
	if (field === "circuityFactor" && typeof value === "number") {
		return `${value.toLocaleString("id-ID", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}×`;
	}
	if (field === "speedKmh" && typeof value === "number") {
		return t("locations.tags.speed", { value: value.toLocaleString("id-ID") });
	}
	return String(value);
}

/** History as a day-grouped feed: only the fields that moved, long text clamped. */
export function HistoryTimeline({
	rows,
	locations,
}: {
	rows: RoutingPolicyChange[];
	locations: RoutingHistoryOptions["locations"];
}) {
	const stateOf = new Map(locations.map((l) => [l.id, l.state]));

	return (
		<ol className="flex flex-col gap-6">
			{groupByDay(rows, (row) => row.changedAt).map((group) => (
				<li key={group.day}>
					<h3 className="mb-3 flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
						{dayLabel(group.day)}
						<span aria-hidden className="h-px flex-1 bg-border" />
					</h3>
					<ol>
						{group.rows.map((row, i) => (
							<Entry
								key={row.id}
								row={row}
								state={
									row.locationId != null ? stateOf.get(row.locationId) : null
								}
								last={i === group.rows.length - 1}
							/>
						))}
					</ol>
				</li>
			))}
		</ol>
	);
}

function Entry({
	row,
	state,
	last,
}: {
	row: RoutingPolicyChange;
	state?: string | null;
	last: boolean;
}) {
	const { t } = useTranslation("routing-mode");
	const isLocation = row.scope === "LOCATION";
	const fields = changedFields(row.oldValue, row.newValue);

	return (
		<li className="grid grid-cols-[2.75rem_0.625rem_minmax(0,1fr)] gap-x-3">
			<time
				dateTime={row.changedAt}
				className="pt-px text-xs tabular-nums text-muted-foreground"
			>
				{timeFormat.format(new Date(row.changedAt))}
			</time>
			<div aria-hidden className="flex flex-col items-center">
				<span
					className={cn(
						"mt-1 size-2.5 shrink-0 rounded-full",
						row.action === "RESET" ? "bg-muted-foreground/60" : "bg-primary",
					)}
				/>
				{!last && <span className="mt-1 w-px flex-1 bg-border" />}
			</div>
			<div className={cn("flex min-w-0 flex-col gap-2", !last && "pb-6")}>
				<div className="flex flex-wrap items-baseline gap-x-2">
					<span className="text-sm font-medium">
						{isLocation ? row.locationCity : t(`history.scope.${row.scope}`)}
					</span>
					<span className="text-xs text-muted-foreground">
						{isLocation ? state : row.env}
					</span>
				</div>

				{row.action === "RESET" ? (
					<p className="text-sm">{t("history.reset")}</p>
				) : fields.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						{t("history.no_change")}
					</p>
				) : (
					<dl className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1">
						{fields.map(({ field, from, to }) => (
							<div key={field} className="contents">
								<dt className="text-xs text-muted-foreground">
									{t(`history.fields.${field}`, { defaultValue: field })}
								</dt>
								<dd className="min-w-0 text-sm">
									{field === "notes" ? (
										to == null ? (
											<span className="text-muted-foreground">
												{t("history.notes_cleared")}
											</span>
										) : (
											<Clamped text={String(to)} />
										)
									) : (
										<span className="flex flex-wrap items-center gap-x-1.5">
											<span className="text-muted-foreground">
												{formatField(field, from, row.scope, t)}
											</span>
											<span aria-hidden className="text-muted-foreground">
												→
											</span>
											<span className="sr-only">{t("history.to")}</span>
											<span className="font-medium">
												{formatField(field, to, row.scope, t)}
											</span>
										</span>
									)}
								</dd>
							</div>
						))}
					</dl>
				)}

				{row.reason ? (
					<div className="border-l-2 pl-3">
						<Clamped text={row.reason} />
					</div>
				) : (
					<p className="text-xs text-muted-foreground">
						{t("history.no_reason")}
					</p>
				)}

				<p className="text-xs text-muted-foreground">
					{row.changedByEmail ?? "—"} ·{" "}
					{t(`history.actor_source.${row.actorSource}`)}
				</p>
			</div>
		</li>
	);
}

// ponytail: "long" is guessed from length/newlines, not measured; a short text in a
// narrow sheet can clamp without a toggle. Measure with a ResizeObserver if that bites.
const LONG_TEXT = 140;

/** Two lines of free text, expandable in place. */
function Clamped({ text }: { text: string }) {
	const { t } = useTranslation("routing-mode");
	const [open, setOpen] = useState(false);
	const long = text.length > LONG_TEXT || text.includes("\n");

	return (
		<div className="flex flex-col items-start gap-0.5">
			<p
				className={cn(
					"whitespace-pre-line break-words text-sm",
					long && !open && "line-clamp-2",
				)}
			>
				{text}
			</p>
			{long && (
				<button
					type="button"
					aria-expanded={open}
					onClick={() => setOpen((o) => !o)}
					className="rounded-sm text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					{open ? t("history.less") : t("history.more")}
				</button>
			)}
		</div>
	);
}
